import { Button, type ButtonProps } from '@/shared/ui/button'

import { type FC, useCallback, useRef } from 'react'

import type { DialogActionProps } from './types'
import { useDialogContext } from './useDialogContext'

/**
 * Individual Action component (button)
 */
export const DialogAction: FC<DialogActionProps> = ({
  intent = 'primary',
  label,
  onClick,
  disabled,
  loading,
  isDefault = false,
  ...restProps
}) => {
  const { onClose, registerDefaultButton } = useDialogContext()
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null)

  const setButtonRef = useCallback(
    (element: HTMLButtonElement | HTMLAnchorElement | null) => {
      buttonRef.current = element
      if (isDefault && element instanceof HTMLButtonElement) {
        registerDefaultButton?.(element)
      }
    },
    [isDefault, registerDefaultButton],
  )

  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      if (onClick) {
        await onClick(event)
      }
      onClose()
    },
    [onClick, onClose],
  )

  const intentMapping: Record<typeof intent, ButtonProps['intent']> = {
    primary: 'primary',
    secondary: 'secondary',
    ghost: 'neutral',
    neutral: 'neutral',
  }

  const variantMapping: Record<typeof intent, ButtonProps['variant']> = {
    primary: 'default',
    secondary: 'default',
    ghost: 'ghost',
    neutral: 'outline',
  }

  return (
    <Button
      ref={setButtonRef}
      intent={intentMapping[intent]}
      variant={variantMapping[intent]}
      label={label}
      onClick={handleClick}
      disabled={disabled}
      loading={loading}
      {...restProps}
    />
  )
}

DialogAction.displayName = 'DialogAction'
